/** Multi-person track picker — inline in Motion Reconstruction node. */

import { CheckOutlined, LoadingOutlined } from '@ant-design/icons'
import { memo, useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  confidenceQualityLabel,
  formatCoveragePercent,
  isLowQualityTrack,
  type MotionTrackSummary,
} from '../motionReconstructionProvider'
import MotionTrackVideoThumb from './MotionTrackVideoThumb'

type Props = {
  tracks: MotionTrackSummary[]
  videoUrl?: string | null
  persistedSelectedTrackId?: string | null
  submitting: boolean
  onConfirm: (trackId: string) => void
}

function MotionTrackSelectionPanelImpl({
  tracks,
  videoUrl,
  persistedSelectedTrackId,
  submitting,
  onConfirm,
}: Props) {
  const { t } = useTranslation('canvas')
  const [pendingTrackId, setPendingTrackId] = useState<string | null>(persistedSelectedTrackId || null)

  const orderedTracks = useMemo(() => [...tracks], [tracks])

  const handleCardClick = (e: MouseEvent, trackId: string) => {
    e.stopPropagation()
    if (submitting) return
    setPendingTrackId(trackId)
  }

  const handleConfirm = (e: MouseEvent) => {
    e.stopPropagation()
    if (!pendingTrackId || submitting) return
    onConfirm(pendingTrackId)
  }

  return (
    <div className="pcv-motion-track-selection nodrag" data-testid="motion-track-selection-panel">
      <p className="pcv-motion-track-selection-title">{t('motionReconstruction.trackSelection.title')}</p>
      <p className="pcv-motion-track-selection-hint">{t('motionReconstruction.trackSelection.hint')}</p>

      <div className="pcv-motion-track-cards">
        {orderedTracks.map((track, index) => {
          const personLabel = t('motionReconstruction.trackSelection.personLabel', { index: index + 1 })
          const selected = pendingTrackId === track.track_id
          const lowQuality = isLowQualityTrack(track)
          const qualityKey = confidenceQualityLabel(track.mean_confidence)
          return (
            <button
              key={track.track_id}
              type="button"
              className={`pcv-motion-track-card${selected ? ' is-selected' : ''}${lowQuality ? ' is-low-quality' : ''}`}
              data-testid={`motion-track-card-${track.track_id}`}
              disabled={submitting}
              onClick={(e) => handleCardClick(e, track.track_id)}
            >
              <div className="pcv-motion-track-card-visual">
                {videoUrl ? (
                  <MotionTrackVideoThumb
                    videoUrl={videoUrl}
                    seekMs={track.representative_timestamp_ms ?? 0}
                    alt={personLabel}
                    className="pcv-motion-track-card-thumb"
                  />
                ) : (
                  <div className="pcv-motion-track-thumb-fallback pcv-motion-track-card-thumb">
                    <span>{index + 1}</span>
                  </div>
                )}
                {selected ? <span className="pcv-motion-track-card-check"><CheckOutlined /></span> : null}
              </div>
              <div className="pcv-motion-track-card-body">
                <span className="pcv-motion-track-card-name">{personLabel}</span>
                <span className="pcv-motion-track-card-meta">
                  {t('motionReconstruction.trackSelection.coverage', {
                    value: formatCoveragePercent(track.coverage),
                  })}
                </span>
                <span className="pcv-motion-track-card-meta">
                  {t(`motionReconstruction.trackSelection.quality.${qualityKey}`)}
                </span>
                {lowQuality ? (
                  <span className="pcv-motion-track-card-warn">
                    {t('motionReconstruction.trackSelection.lowQualityHint')}
                  </span>
                ) : null}
                <span className="pcv-motion-track-card-id">
                  {t('motionReconstruction.trackSelection.trackId', { id: track.track_id })}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {persistedSelectedTrackId ? (
        <p className="pcv-motion-track-selection-current">
          {t('motionReconstruction.trackSelection.currentSelection', {
            index: orderedTracks.findIndex((t) => t.track_id === persistedSelectedTrackId) + 1 || '?',
          })}
        </p>
      ) : null}

      <button
        type="button"
        className="pcv-gen-run-btn pcv-motion-track-confirm-btn"
        data-testid="motion-track-confirm-btn"
        disabled={!pendingTrackId || submitting}
        onClick={handleConfirm}
      >
        {submitting ? <LoadingOutlined spin /> : null}{' '}
        {submitting
          ? t('motionReconstruction.trackSelection.confirming')
          : t('motionReconstruction.trackSelection.confirm')}
      </button>
    </div>
  )
}

export default memo(MotionTrackSelectionPanelImpl)
